import { $ } from '@deqo/foundation';
import { Connection, Podopolis, PodopolisParams } from '@deqo/podopolis';
import { camelCase } from 'camel-case';
import { Container } from 'inversify';
import { throttle } from 'throttle-debounce';
import { LocalCursorBundle } from '../@resources/cursors/LocalCursorBundle';
import { GoogleFontBundle } from '../@resources/fonts/GoogleFontBundle';
import { LocalFontBundle } from '../@resources/fonts/LocalFontBundle';
import { LocalIconBundle } from '../@resources/icons/LocalIconBundle';
import { StreamlineIconBundle } from '../@resources/icons/StreamlineIconBundle';
import { LocalSoundBundle } from '../@resources/sounds/LocalSoundBundle';
import { AnnounceKit } from '../@vendor/AnnounceKit';
import { IntercomCtrl } from '../@vendor/Intercom';
import { telemetry } from '../@vendor/Posthog';
import { listen, waitFor } from '../CodesThatsGottaGoSomewhereElse';
import { Host } from '../Host';
import { ApiClient } from '../api/ApiClient';
import { getAccessToken } from '../api/ApiEndpoints';
import { v2 } from '../api/ApiInterfaceV2';
import { ApiRefreshToken } from '../api/ApiRefreshToken';
import { Url } from '../platform/Url';
import { AssetBundle } from '../platform/assets/AssetBundle';
import { accountPlanChangedPopup } from './AccountPlanChangedPopup';
import { AppEvents } from './AppEvents';
import { AppLocation } from './AppLocation';
import { MetricsHub } from './MetricsHub';
import { PassportModel } from './PassportModel';
import { ProfileModel } from './ProfileModel';
import { roleChangedPopup } from './RoleChangedPopup';
import { createScope } from './Scope';
import { AccountModel } from './account/AccountModel';
import { BoardUsageModel } from './account/BoardUsageModel';
import { HelpController } from './core/help/HelpController';
import { DefaultHangout } from './hangouts/DefaultHangout';
import { Hangout } from './hangouts/Hangout';
import { NullHangout } from './hangouts/NullHangout';
import { boardWelcomePopup } from './onboarding/BoardWelcomePopup';
import { TourManager } from './onboarding/TourManager';
import { PopupManager } from './popups/PopupManager';
import { PreferencesModel } from './preferences/PreferencesModel';
import { PreferencesManager } from './profile/PreferencesManager';
import { preferencesPopup } from './profile/PreferencesPopup';

const installConnectivity = (scope: Container) => {
    const art = ApiRefreshToken.resolve();
    if (!art) {
        if (Host.env === 'local') {
            window.location.href = `${Host.baseUrl}/login?source=${window.location.pathname}`;
        }
        else {
            throw new Error('Could not load security token.');
        }
    }

    const api = new ApiClient(art);
    scope.bind(ApiClient).toConstantValue(api);

    const connectionFactory = async () => {
        const apiToken = ApiRefreshToken.resolve();
        const { accessToken } = await api.exec(getAccessToken(apiToken.value));
        return new Connection(Host.collabUrl, accessToken);
    };

    const params: PodopolisParams = {
        connectionFactory,
        backoffPolicy: {
            base: 500,
            multipler: 1.5,
            max: 60000
        },
        pingPolicy: $(2, 5000),
    };

    const podopolis = new Podopolis(params);
    podopolis.start();
    scope.bind(Podopolis).toConstantValue(podopolis);

    return Promise.resolve([podopolis]);
};

const installAppLevelModels = async (scope: Container) => {

    const api = scope.get(ApiClient);
    const evts = scope.get(AppEvents);

    // Do this before the other calls because it will prevent multiple request_access
    const [profile, refreshProfile] = await ProfileModel.load(() => api.exec(v2.me.info()));
    const [preferences, refreshPreferences] = await PreferencesModel.load(() => api.exec(v2.me.preferences.info()));
    const [account, refreshAccount] = await AccountModel.load(() => api.exec(v2.me.home()));
    const passport = PassportModel.create(await api.exec(v2.me.permissions()));

    const boardLimit = account.active && !!account.plan.features.boardLimit && profile.id !== 'US000000ANON'
        ? BoardUsageModel.create(await api.exec(v2.me.boardUsage()))
        : BoardUsageModel.create();

    scope.bind(AccountModel).toConstantValue(account);
    scope.bind(BoardUsageModel).toConstantValue(boardLimit);
    scope.bind(PreferencesModel).toConstantValue(preferences);
    scope.bind(ProfileModel).toConstantValue(profile);
    scope.bind(PassportModel).toConstantValue(passport);
    scope.bind(TourManager).toSelf().inSingletonScope();
    scope.bind(PreferencesManager).toSelf().inSingletonScope();
    scope.bind(HelpController).toSelf().inSingletonScope();

    // TODO:
    // This is lazy and kinda kak; should navigation have it's own sub scope?
    // Yes it fucking should this is getting worse

    scope.bind(MetricsHub).toSelf().inSingletonScope();
    const metrics = scope.get(MetricsHub);
    await metrics.start();

    return [
        metrics,
        evts.on('account:patch', (data) => {
            refreshAccount();
        }),
        evts.on('workspace:patch', ({ workspace }) => {
            refreshAccount();
        }),
        // If local user added to or removed from workspace, update home
        evts.on('workspace:members:add', ({ user }) => {
            if (profile.id === user.id) refreshAccount();
        }),
        evts.on('workspace:members:remove', ({ userId }) => {
            if (profile.id === userId) refreshAccount();
        }),
        evts.on('workspace:create', () => {
            refreshAccount();
        }),
        evts.on('workspace:delete', () => {
            refreshAccount();
        }),
        evts.on('folder:create', ({ folder }) => {
            if (!account.workspaces.flatMap(x => x.folders).find(x => x.id === folder.id)) refreshAccount();
        }),
        evts.on('folder:patch', ({ folder }) => {
            if (!!account.workspaces.flatMap(x => x.folders).find(x => x.id === folder.id)) refreshAccount();
        }),
        evts.on('folder:delete', ({ folderId }) => {
            if (!!account.workspaces.flatMap(x => x.folders).find(x => x.id === folderId)) refreshAccount();
        }),
        evts.on('board:create', ({ board }) => {
            // Temporarily add board active boards (bit of a hack)
            if (boardLimit.limit > 0) {
                boardLimit.mutate(x => x.boards.push(board));
            }
        }),
        // If self user patched, update model
        evts.on('user:patch', ({ userId }) => {
            if (userId === profile.id) {
                refreshProfile();
                refreshPreferences();
            }
        }),
    ];
};

const loadIntegrations = async (scope: Container) => {

    const account = scope.get(AccountModel);
    const profile = scope.get(ProfileModel);

    if (account.active && (account.metadata?.intercom ?? true)) {
        IntercomCtrl.init(profile);
    }

    AnnounceKit.init();

    return Promise.resolve([]);
};

const loadStaticData = async (scope: Container) => {

    await AssetBundle.loadAll(
        new GoogleFontBundle(),
        new LocalCursorBundle(),
        new LocalFontBundle(),
        new LocalIconBundle(),
        new LocalSoundBundle(),
        new StreamlineIconBundle(),
    );

    return [];
};

const installAnalytics = (scope: Container) => {

    const evts = scope.get(AppEvents);
    const location = scope.get(AppLocation);
    const profile = scope.get(ProfileModel);
    const account = scope.get(AccountModel);

    telemetry.login(profile.id, profile.name, profile.email, account.id, account.label);

    const extractIds = (input: any) => {
        const output = {} as any;
        for (const k of Object.keys(input)) {
            if (k.endsWith('Id')) {
                output[camelCase(k)] = input[k];
            }
            else if (typeof (input[k]) === 'object' && 'id' in input[k]) {
                output[camelCase(`${k}Id`)] = input[k].id;
            }
        }
        return output;
    };

    return Promise.resolve([
        // On page change, submit pageview
        location.observe(throttle(1000, () => telemetry.pageView(), true)),
        // On all events, submit to posthog
        evts.on('*', (data, name) => {
            if (name.startsWith('board') || name.startsWith('folder') || name.startsWith('workspace')) {
                if (name.startsWith('preferences')) return;
                if (Array.isArray(data)) return;
                name = name.replace(/\:/g, '.');
                if (!(typeof (data) === 'object' && !!(data as any).remote)) {
                    telemetry.emit(name, extractIds(data));
                }
            }
        }),
    ]);

};

const loadHangout = async (scope: Container) => {

    const account = scope.get(AccountModel);
    const profile = scope.get(ProfileModel);

    const hangout = account.active && !Host.offline && !profile.isAnonymous ? (
        new DefaultHangout(
            scope.get(AccountModel),
            scope.get(ApiClient),
            scope.get(AppLocation),
            scope.get(Podopolis),
        )
    ) : (
        new NullHangout()
    );

    scope.bind(Hangout).toConstantValue(hangout);
    // window['hangout'] = hangout;

    return Promise.resolve([
        hangout,
        listen(window, 'focus', () => hangout.updateMyLocation())
    ]);
};

const loadDynamicStuffThatShouldProbablyBeRefactored = async (scope: Container) => {

    const api = scope.get(ApiClient);
    const events = scope.get(AppEvents);
    const boardUsage = scope.get(BoardUsageModel);
    const me = scope.get(ProfileModel);
    const hangout = scope.get(Hangout);
    const popups = scope.get(PopupManager);

    return Promise.resolve([
        events.on('ready', () => {
            const autoShow = Url.consumeParam('popup') ?? null;
            if (autoShow === 'welcome') {
                popups.invoke(boardWelcomePopup());
            }
            if (autoShow?.startsWith('preferences')) {
                const [_, option] = autoShow.split(':');
                popups.invoke(preferencesPopup(option));
            }
        }),
        events.on('ready', () => {
            // popups.invoke(meetingFeedbackThanksPopup());
        }),
        // events.on('push', ([type, data]) => {
        //     hangout.sendRelay('*', type, data);
        // }),
        hangout.on('clientRelay', async ([sender, type, data]) => {
            // If we sent this relay, ignore it
            if (sender === hangout.self) return;
            if (type === 'plan_changed') {
                await popups.invoke(accountPlanChangedPopup());
                window.location.reload();
            }
            if (type === 'role_changed' && data.userId === me.id) {
                await popups.invoke(roleChangedPopup());
                window.location.reload();
            }
            else if (type === 'board_usage_changed') {
                const update = !!data ? data : await api.exec(v2.me.boardUsage());
                boardUsage.mutate(update);
            }
            // TODO: Holy shit this whole AppEvents vs server AppMessages is fucked and needs straightening out but for now yolo if there is
            // a colon (:) it's an AppEvent for the pod from the server
            //
            else if (type.includes(':')) {
                events.recv(type as any, { ...data, remote: true });
            }
        }),
    ]);
};

export const AppScope = createScope({
    traceAs: 'AppScope',
    refresh: async (p, scope) => {
        await waitFor(50);
        return [

            // Api and Podopolis
            ...(await installConnectivity(scope)),

            // Top level models
            ...(await installAppLevelModels(scope)),

            // Analytics
            ...(await installAnalytics(scope)),

            // Integrations
            ...(await loadIntegrations(scope)),

            // Static data
            ...(await loadStaticData(scope)),

            // Hangout
            ...(await loadHangout(scope)),

            // Hmmm
            ...(await loadDynamicStuffThatShouldProbablyBeRefactored(scope)),
        ];
    },
});
